import { keepPreviousData, queryOptions, useQueries, useQuery } from '@tanstack/react-query'

import { customerQueryOptions } from '@/features/customers/queries'
import {
  getGroup,
  getGroupHealth,
  listGroupMeetings,
  listGroups,
  type GroupListQuery,
  type GroupMeetingQuery,
} from '@/api/groups-api'
import type { GroupMember, KycStatus } from '@/api/types'

import { groupKeys } from './group-keys'

/**
 * Reads over the group module.
 *
 * Paging and filtering are the server's job throughout, for the same reason they are on the
 * customer list: a branch's groups do not fit in a browser, and rows the caller may not see must
 * never be removed client-side from a page that already contains them.
 */

export const groupListQueryOptions = (query: GroupListQuery) =>
  queryOptions({
    queryKey: groupKeys.list(query),
    queryFn: () => listGroups(query),
    // The previous page stays on screen while the next one is fetched, so the table can be read
    // while it is being filtered rather than blanking on every keystroke.
    placeholderData: keepPreviousData,
  })

export const groupQueryOptions = (groupId: string) =>
  queryOptions({
    queryKey: groupKeys.detail(groupId),
    queryFn: () => getGroup(groupId),
  })

/**
 * The stored health snapshot on its own.
 *
 * `staleTime: 0` because this is the figure a next-round decision turns on, and a score read from
 * a cache filled before the last recompute is the one way this screen could mislead someone into
 * granting a round the group no longer qualifies for.
 */
export const groupHealthQueryOptions = (groupId: string) =>
  queryOptions({
    queryKey: groupKeys.health(groupId),
    queryFn: () => getGroupHealth(groupId),
    staleTime: 0,
  })

export const groupMeetingsQueryOptions = (groupId: string, query: GroupMeetingQuery) =>
  queryOptions({
    queryKey: groupKeys.meetings(groupId, query),
    queryFn: () => listGroupMeetings(groupId, query),
    placeholderData: keepPreviousData,
  })

/**
 * The API's own maximum page size. Asking for more is refused as `groups.page_size_out_of_range`,
 * so it is written down here once rather than guessed at each call site.
 */
export const MEETING_PAGE_SIZE = 100

/**
 * One meeting, by its id.
 *
 * There is no endpoint that reads a single meeting, and a schedule may put up to 104 of them on
 * the calendar — more than one page holds. So this walks the group's own calendar a page at a
 * time until it finds the one asked for, and answers `null` when the calendar does not contain it:
 * a cancelled meeting, or a link that belongs to another group. Both are real answers the
 * attendance screen has to distinguish from a failed read.
 */
export const groupMeetingQueryOptions = (groupId: string, meetingId: string) =>
  queryOptions({
    queryKey: [...groupKeys.meetingLists(groupId), 'one', meetingId] as const,
    queryFn: async () => {
      for (let page = 1; ; page += 1) {
        const result = await listGroupMeetings(groupId, { page, pageSize: MEETING_PAGE_SIZE })

        const found = result.items.find((meeting) => meeting.id === meetingId)
        if (found) {
          return found
        }

        if (page >= result.totalPages || result.items.length === 0) {
          return null
        }
      }
    },
  })

export const useGroupMeeting = (groupId: string, meetingId: string) =>
  useQuery(groupMeetingQueryOptions(groupId, meetingId))

export const useGroupList = (query: GroupListQuery) => useQuery(groupListQueryOptions(query))

export const useGroup = (groupId: string) => useQuery(groupQueryOptions(groupId))

export const useGroupHealth = (groupId: string) => useQuery(groupHealthQueryOptions(groupId))

export const useGroupMeetings = (groupId: string, query: GroupMeetingQuery) =>
  useQuery(groupMeetingsQueryOptions(groupId, query))

/**
 * Each member's current KYC state, read from the Customers module.
 *
 * The group payload deliberately does not carry it. A `GroupMember` is a membership, not a copy of
 * a customer file, and the contract says so in as many words — a member list that carried KYC,
 * phone, and card number would be a second, unguarded copy of the record the Customers module
 * masks and gates.
 *
 * It is still worth showing, because approved KYC is a condition of *joining* and not a condition
 * of *remaining*: amending an approved customer's Ghana Card number or date of birth returns their
 * file to `PendingReview` while they carry on being a member of this group. A screen that showed
 * only the membership status would present that group as fully in order.
 *
 * So it is asked for per member, and only when the members panel is actually on screen. The
 * queries share the customer detail cache, so opening one of these members afterwards costs
 * nothing. `undefined` is returned for a member whose file could not be read — a customer at a
 * branch this user cannot see, most often — and the screen says that rather than guessing.
 */
export interface MemberKycState {
  customerId: string
  kycStatus: KycStatus | undefined
  isPending: boolean
  isUnavailable: boolean
}

export function useMemberKycStates(members: GroupMember[], enabled: boolean): MemberKycState[] {
  return useQueries({
    queries: members.map((member) => ({
      ...customerQueryOptions(member.customerId),
      enabled,
      // A KYC decision is not a per-second fact, and this fires once per member. Five minutes is
      // short enough that a file sent back for review this morning shows here this morning.
      staleTime: 5 * 60 * 1000,
    })),
    combine: (results) =>
      results.map((result, index) => ({
        customerId: members[index]?.customerId ?? '',
        kycStatus: result.data?.kycStatus,
        isPending: result.isPending && enabled,
        isUnavailable: result.isError,
      })),
  })
}
